import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import { CustomTheme, greyMultibrandPalette } from '@masfront/common-b2c'

import { Box, makeStyles, Button, Typography } from '@material-ui/core'

import OffersConfiguratorCard from './OffersConfiguratorCard'

import BonusesAvailableModal from '~/bonus/components/BonusesAvailableModal'
import { useBrand } from '~/brand'
import { useAppDepsContext } from '~/core/hooks/useAppDepsContext'
import useUpdatedCustomer from '~/core/hooks/useUpdatedCustomer'
import { ChangeEmailModal } from '~/customer/components'
import { mergeFixedAndInternetSubscriptions, getAccountFromAccountId } from '~/customer/helpers'
import { Customer, Package } from '~/customer/model/customer.model'
import useDownloadContract from '~/offersConfigurator/hooks/useDownloadContract'
import { SALES_PATHS } from '~/pages/router/paths'
import Permission from '~/permissions/model/permissions.model'
import { selectHasPermission } from '~/permissions/store/permissions.selectors'
import { B2CModal } from '~/ui/components'
import GridTemplate3xN from '~/ui/components/GridTemplate3xN/GridTemplate3xN'
import { MainTitleHeader } from '~/ui/components/MainTitleHeader'

const useStyles = makeStyles((theme: CustomTheme) => ({
  titleList: {
    display: 'flex',
    padding: 0,
    margin: 0,
    lineHeight: '0.9',
    '& li': {
      display: 'flex',
      flexDirection: 'row',
      padding: theme.spacing(0, 3),
      borderLeft: `2px ${greyMultibrandPalette[900]} solid`,
    },
    '& li:first-child': {
      paddingLeft: 0,
      borderLeft: 'none',
    },
  },
  newContractButton: {
    width: '100%',
    height: theme.spacing(16),
    backgroundColor: theme.palette.common.white,
    textTransform: 'initial',
    '& .MuiButton-label': {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  buttonLoading: {
    '&:hover': {
      cursor: 'wait',
    },
  },
}))

interface OffersConfiguratorProps {
  customer: Customer
  packages?: Package[]
  accountNumber?: string
}

const OffersConfigurator: React.FC<OffersConfiguratorProps> = ({
  customer,
  packages,
  accountNumber,
}) => {
  const styles = useStyles()
  const { brand } = useBrand()
  const hasPermissionsSellPortfolio = useSelector(selectHasPermission(Permission.SELL_PORTFOLIO))
  const { runtimeConfig } = useAppDepsContext()
  const { onUpdateCustomer } = useUpdatedCustomer()

  const {
    downloadContract,
    downloadContractState,
    cleanDownloadContractError,
  } = useDownloadContract()
  const contractLoading = downloadContractState.loading
  const contractError = downloadContractState.error

  const currentAccount = getAccountFromAccountId(customer, accountNumber)

  const subscriptionId = currentAccount?.packages[0].subscriptions[0].id
  const accountId = currentAccount?.id
  const hasAccountContract = currentAccount?.hasContract

  const actions = [
    {
      label: !contractLoading ? 'Descargar contrato' : undefined,
      onClick: () => downloadContract(subscriptionId, accountId),
      disabled: !hasAccountContract,
      isLoading: contractLoading,
    },
  ]

  const [isBonusesAvailableModalOpen, setIsOpenBonusesAvailableModal] = useState(false)
  const [subscriptionIdInModal, setSubscriptionIdInModal] = useState<string>('')
  const [isOpenChangeEmailModal, setIsOpenChangeEmailModal] = useState(false)

  const document = customer?.documentValue
  const documentType = customer?.documentType
  const newContractURL = `${runtimeConfig.salesHost}${SALES_PATHS.SALES_START}?brand=${brand}&document=${document}&documentType=${documentType}`

  const changeEmailModalOnClose = (emailHasChanged: boolean) => {
    setIsOpenChangeEmailModal(false)
    if (emailHasChanged) {
      onUpdateCustomer()
    }
  }

  return (
    <>
      <Box
        bgcolor={greyMultibrandPalette[50]}
        height="100%"
        overflow="auto"
        data-hook={`offers-configurator-${accountNumber}`}
      >
        <MainTitleHeader title="Configurador de ofertas" actions={actions} />
        <GridTemplate3xN>
          {packages?.map((pkg) =>
            mergeFixedAndInternetSubscriptions(pkg.subscriptions).map((subscription) => (
              <OffersConfiguratorCard
                key={subscription.id}
                customer={customer}
                subscription={subscription}
                setSubscriptionIdInModal={setSubscriptionIdInModal}
                setIsOpenBonusesAvailableModal={setIsOpenBonusesAvailableModal}
              />
            )),
          )}
          {hasPermissionsSellPortfolio && (
            <Box width="100%" gridColumn="1 / span 3" gridRow="auto / auto">
              <Button
                className={`${styles.newContractButton} ${
                  contractLoading ? styles.buttonLoading : ''
                }`}
                variant="outlined"
                color="secondary"
                size="medium"
                onClick={() => window.open(newContractURL, '_self')}
                data-hook="new-contract-button"
              >
                <Typography variant="h3">+ Nuevo contrato</Typography>
              </Button>
            </Box>
          )}
          <Box width="100%" gridColumn="1 / span 3" gridRow="auto / auto">
            <Button
              className={`${styles.newContractButton} ${
                contractLoading ? styles.buttonLoading : ''
              }`}
              variant="outlined"
              color="secondary"
              size="medium"
              onClick={() => setIsOpenChangeEmailModal(true)}
              data-hook="new-energy-contract-button"
            >
              <Typography variant="h3">+ Añadir contrato de energía</Typography>
            </Button>
          </Box>
        </GridTemplate3xN>
      </Box>
      {customer && isBonusesAvailableModalOpen && (
        <BonusesAvailableModal
          subscriptionId={subscriptionIdInModal}
          isOpen={isBonusesAvailableModalOpen}
          onClose={() => setIsOpenBonusesAvailableModal(false)}
        />
      )}
      <B2CModal
        isOpen={!!contractError}
        type="error"
        title="No se ha podido descargar el contrato"
        onClose={cleanDownloadContractError}
      >
        <Box m={15} />
      </B2CModal>
      {isOpenChangeEmailModal && (
        <ChangeEmailModal isOpen={isOpenChangeEmailModal} onClose={changeEmailModalOnClose} />
      )}
    </>
  )
}

export default OffersConfigurator
